import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Zahlung wird verarbeitet...');
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      const registrationId = searchParams.get('registration_id');
      const token = searchParams.get('token'); // PayPal order ID
      
      if (!registrationId) {
        setStatus('error');
        setMessage('Buchungs-ID fehlt');
        return;
      }

      try {
        // First, get the registration to find the PayPal order ID
        const regResponse = await fetch(`${API_URL}/api/admin/registrations`, {
          headers: {
            'Authorization': 'Basic ' + btoa('admin:EHBottrop2025!')
          }
        });
        
        let orderId = token;
        
        if (regResponse.ok) {
          const allRegs = await regResponse.json();
          const reg = allRegs.find(r => r.id === registrationId);
          if (reg) {
            setRegistration(reg);
            orderId = orderId || reg.paypal_order_id;
            
            // If already paid, show success
            if (reg.payment_status === 'paid') {
              setStatus('success');
              setMessage('Ihre Zahlung wurde erfolgreich bestätigt!');
              return;
            }
          }
        }

        if (!orderId) {
          setStatus('error');
          setMessage('PayPal Bestellnummer fehlt');
          return;
        }

        // Capture the payment
        const captureResponse = await fetch(`${API_URL}/api/paypal/capture-order/${orderId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (captureResponse.ok) {
          const result = await captureResponse.json();
          setStatus('success');
          
          // Check if this was already processed (page reload)
          if (result.already_processed) {
            setMessage('Ihre Zahlung wurde bereits bestätigt!');
          } else {
            setMessage('Ihre Zahlung wurde erfolgreich bestätigt!');
          }
          
          // Update registration data
          if (result.registration_id) {
            const updatedReg = await fetch(`${API_URL}/api/admin/registrations`, {
              headers: {
                'Authorization': 'Basic ' + btoa('admin:EHBottrop2025!')
              }
            });
            if (updatedReg.ok) {
              const allRegs = await updatedReg.json();
              const reg = allRegs.find(r => r.id === result.registration_id);
              if (reg) setRegistration(reg);
            }
          }
        } else {
          const errorData = await captureResponse.json();
          
          // Check if it's because payment was already captured
          if (errorData.detail?.includes('COMPLETED') || errorData.detail?.includes('already') || errorData.detail?.includes('bereits')) {
            setStatus('success');
            setMessage('Ihre Zahlung wurde bereits bestätigt!');
          } else if (errorData.detail?.includes('TRANSACTION_REFUSED') || errorData.detail?.includes('refused')) {
            // Payment was not completed at PayPal
            setStatus('error');
            setMessage('Die Zahlung wurde nicht abgeschlossen. Bitte versuchen Sie es erneut.');
          } else {
            setStatus('error');
            setMessage(errorData.detail || 'Fehler bei der Zahlungsbestätigung');
          }
        }
      } catch (error) {
        console.error('Payment capture error:', error);
        setStatus('error');
        setMessage('Ein Fehler ist aufgetreten. Bitte kontaktieren Sie uns.');
      }
    };

    capturePayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      <main className="flex-grow flex items-center justify-center py-16 px-4">
        <div className="max-w-lg w-full text-center">
          {status === 'processing' && (
            <div className="space-y-6">
              <Loader2 className="w-20 h-20 text-blue-600 mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900">Zahlung wird verarbeitet</h1>
              <p className="text-slate-600">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900">Vielen Dank!</h1>
              <p className="text-lg text-slate-600">{message}</p>
              
              {registration && (
                <div className="bg-slate-50 rounded-lg p-6 text-left mt-8">
                  <h2 className="font-semibold text-slate-900 mb-4">Ihre Buchungsdetails:</h2>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Name:</dt>
                      <dd className="font-medium">{registration.first_name} {registration.last_name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Kurs:</dt>
                      <dd className="font-medium">{registration.package_name || 'Erste Hilfe Kurs'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Datum:</dt>
                      <dd className="font-medium">{registration.course_date}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Uhrzeit:</dt>
                      <dd className="font-medium">{registration.course_time}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Ort:</dt>
                      <dd className="font-medium">Poststraße 6, 46236 Bottrop</dd>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <dt className="text-slate-500">Betrag:</dt>
                      <dd className="font-bold text-green-600">{registration.amount?.toFixed(2)} € (bezahlt)</dd>
                    </div>
                  </dl>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4 mt-6">
                <p className="text-blue-800 text-sm">
                  Eine Bestätigungs-E-Mail wurde an <strong>{registration?.email}</strong> gesendet.
                </p>
              </div>

              <div className="pt-6">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white px-8"
                >
                  Zurück zur Startseite
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="bg-red-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto">
                <XCircle className="w-16 h-16 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Zahlung nicht abgeschlossen</h1>
              <p className="text-slate-600">{message}</p>
              
              {message?.includes('nicht abgeschlossen') && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Tipp:</strong> Bitte stellen Sie sicher, dass Sie bei PayPal auf <strong>"Jetzt bezahlen"</strong> klicken und die Zahlung vollständig abschließen, bevor Sie hierher zurückkehren.
                  </p>
                </div>
              )}
              
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  Bei Fragen kontaktieren Sie uns unter <strong>info@erstehilfebottrop.de</strong> oder <strong>0176 8015 1197</strong>
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => navigate('/termine')}
                  className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white"
                >
                  Neue Buchung starten
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Zur Startseite
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
